import {
  BlockType,
  buildRawBlockString,
  type FilterConfig,
  FilterOperator,
  FilterQueryParser,
  getFilterValue,
  getProviderKey,
  type IndexedAnyBlock,
  type IndexedFilterBlock,
  type IndexedGroupBlock,
  type IndexedKeywordBlock,
  isIndexedAnyBlock,
} from '@github-ui/filter-core'

import {createParserProvider} from './create-parser-provider'
import {cleanConsecutiveSpaces, type TokenType} from './query-values'
import {MAX_QUERY_LENGTH} from './tokenize-query'

const structuralProviderKeys = [
  'label',
  'project',
  'author',
  'assignee',
  'milestone',
  'review',
  'reviewed-by',
  'user-review-requested',
  'review-requested',
  'type',
  'no',
] as const

const groupingSupportConfig: FilterConfig = {
  filterDelimiter: ':',
  valueDelimiter: ',',
  variant: 'input',
  groupAndKeywordSupport: true,
}

type BlockContainer = {
  blocks: IndexedAnyBlock[]
  group?: IndexedGroupBlock
  parent?: BlockContainer
}

type FilterBranch = {
  block: IndexedFilterBlock
  container: BlockContainer
}

type BranchOperand = {
  block: IndexedFilterBlock | IndexedGroupBlock
  container: BlockContainer
}

export type FilterBranchInspection = {
  key: string
  values: string[]
  isNegated: boolean
}

export type FilterBranchQueryInspection = {
  isSafe: boolean
  hasBooleanStructure: boolean
  branches: FilterBranchInspection[]
}

type StructuralQueryInspection = Omit<FilterBranchQueryInspection, 'branches'> & {
  blocks: IndexedAnyBlock[]
}

function significantBlocks(blocks: IndexedAnyBlock[]) {
  return blocks.filter(block => block.type !== BlockType.Space)
}

function isBooleanKeyword(block: IndexedAnyBlock | undefined): block is IndexedKeywordBlock {
  return block?.type === BlockType.Keyword && (block.raw.toUpperCase() === 'AND' || block.raw.toUpperCase() === 'OR')
}

function hasUnsafeStructure(blocks: IndexedAnyBlock[]): boolean {
  const significant = significantBlocks(blocks)
  if (significant.length === 0) return false

  let expectsOperand = true
  let hasImplicitAnd = false
  const operators = new Set<string>()
  for (const block of significant) {
    if (block.type === BlockType.UnmatchedOpenParen || block.type === BlockType.UnmatchedCloseParen) return true
    if (
      block.type === BlockType.Group &&
      (significantBlocks(block.blocks).length === 0 || hasUnsafeStructure(block.blocks))
    ) {
      return true
    }

    if (isBooleanKeyword(block)) {
      if (expectsOperand) return true
      operators.add(block.raw.toUpperCase())
      expectsOperand = true
    } else {
      if (!expectsOperand) hasImplicitAnd = true
      expectsOperand = false
    }
  }

  return expectsOperand || (operators.has('OR') && (operators.has('AND') || hasImplicitAnd))
}

function containsBooleanStructure(blocks: IndexedAnyBlock[]): boolean {
  return blocks.some(block => block.type === BlockType.Group || isBooleanKeyword(block))
}

function isEscapedCharacter(value: string, index: number): boolean {
  let precedingBackslashes = 0
  for (let previousIndex = index - 1; value[previousIndex] === '\\'; previousIndex--) precedingBackslashes++
  return precedingBackslashes % 2 !== 0
}

function hasUnsafeQuote(searchQuery: string): boolean {
  let inQuotes = false

  for (let index = 0; index < searchQuery.length; index++) {
    const character = searchQuery[index]
    if (character === '"') {
      if (isEscapedCharacter(searchQuery, index)) return true
      inQuotes = !inQuotes
    }
  }

  return inQuotes
}

function containsFilterOrBooleanStructure(blocks: IndexedAnyBlock[]): boolean {
  return blocks.some(
    block =>
      block.type === BlockType.Filter ||
      block.type === BlockType.Keyword ||
      (block.type === BlockType.Group && containsFilterOrBooleanStructure(block.blocks)),
  )
}

function maskBalancedFilterValueParentheses(searchQuery: string, parser: FilterQueryParser): string | undefined {
  const maskedQuery = searchQuery.split('')
  let inQuotes = false
  let tokenStart = 0

  const maskToken = (start: number, end: number): boolean => {
    const filterDelimiterIndex = searchQuery.indexOf(groupingSupportConfig.filterDelimiter, start)
    if (filterDelimiterIndex < start || filterDelimiterIndex >= end) return true

    const openParentheses: number[] = []
    const pairs: Array<[number, number]> = []
    let tokenInQuotes = false

    for (let index = filterDelimiterIndex + 1; index < end; index++) {
      const character = searchQuery[index]
      if (character === '"' && !isEscapedCharacter(searchQuery, index)) {
        tokenInQuotes = !tokenInQuotes
      } else if (!tokenInQuotes && character === '(') {
        openParentheses.push(index)
      } else if (!tokenInQuotes && character === ')' && openParentheses.length > 0) {
        const openIndex = openParentheses.pop()
        if (openIndex !== undefined) pairs.push([openIndex, index])
      }
    }

    for (const [openIndex, closeIndex] of pairs) {
      const blocks = parser.parse(searchQuery.slice(openIndex + 1, closeIndex)).blocks
      if (!blocks.every(isIndexedAnyBlock) || containsFilterOrBooleanStructure(blocks)) continue

      maskedQuery[openIndex] = '['
      maskedQuery[closeIndex] = ']'
    }
    return openParentheses.length === 0
  }

  for (let index = 0; index < searchQuery.length; index++) {
    const character = searchQuery[index]
    if (character === '"' && !isEscapedCharacter(searchQuery, index)) inQuotes = !inQuotes
    if (/\s/.test(character ?? '') && !inQuotes && tokenStart < index) {
      if (!maskToken(tokenStart, index)) return undefined
      tokenStart = index + 1
    } else if (/\s/.test(character ?? '') && !inQuotes) {
      tokenStart = index + 1
    }
  }

  if (tokenStart < searchQuery.length && !maskToken(tokenStart, searchQuery.length)) return undefined

  return maskedQuery.join('')
}

function splitFilterValues(rawValues: string): string[] {
  const values: string[] = []
  let currentValue = ''
  let inQuotes = false

  for (let index = 0; index < rawValues.length; index++) {
    const character = rawValues[index] ?? ''
    if (character === '"' && !isEscapedCharacter(rawValues, index)) inQuotes = !inQuotes
    if (character === groupingSupportConfig.valueDelimiter && !inQuotes) {
      values.push(currentValue)
      currentValue = ''
    } else {
      currentValue += character
    }
  }

  values.push(currentValue)
  return values
}

function normalizeFilterValue(value: string | null | undefined): string {
  const trimmed = value?.trim() ?? ''
  return trimmed.startsWith('"') && trimmed.endsWith('"') ? trimmed.slice(1, -1).trim() : trimmed
}

function isSemanticallyEmptyValue(value: string | null | undefined): boolean {
  return normalizeFilterValue(value) === ''
}

function containsEmptyFilter(blocks: IndexedAnyBlock[]): boolean {
  return blocks.some(
    block =>
      (block.type === BlockType.Filter &&
        (block.value.values.length === 0 ||
          block.value.values.some(value => isSemanticallyEmptyValue(getFilterValue(value.value))))) ||
      (block.type === BlockType.Text &&
        block.raw.indexOf(groupingSupportConfig.filterDelimiter) > 0 &&
        splitFilterValues(block.raw.slice(block.raw.indexOf(groupingSupportConfig.filterDelimiter) + 1)).some(
          isSemanticallyEmptyValue,
        )) ||
      (block.type === BlockType.Group && containsEmptyFilter(block.blocks)),
  )
}

function isNoValueBranch(block: IndexedFilterBlock, types: readonly TokenType[]): boolean {
  return (
    getProviderKey(block.key.value).toLowerCase() === 'no' &&
    block.value.values.length === 1 &&
    types.includes(normalizeFilterValue(getFilterValue(block.value.values[0]?.value)).toLowerCase() as TokenType)
  )
}

function containsAmbiguousNoValueBranch(blocks: IndexedAnyBlock[], types: readonly TokenType[]): boolean {
  return blocks.some(
    block =>
      (block.type === BlockType.Filter &&
        getProviderKey(block.key.value).toLowerCase() === 'no' &&
        block.value.values.length !== 1 &&
        block.value.values.some(value =>
          types.includes(normalizeFilterValue(getFilterValue(value.value)).toLowerCase() as TokenType),
        )) ||
      (block.type === BlockType.Group && containsAmbiguousNoValueBranch(block.blocks, types)),
  )
}

function findFilterBranches(blocks: IndexedAnyBlock[], types: readonly TokenType[]): FilterBranch[] {
  const branches: FilterBranch[] = []
  const root: BlockContainer = {blocks}

  const visit = (container: BlockContainer) => {
    for (const block of container.blocks) {
      if (block.type === BlockType.Group) {
        visit({blocks: block.blocks, group: block, parent: container})
      } else if (
        block.type === BlockType.Filter &&
        (types.includes(getProviderKey(block.key.value).toLowerCase() as TokenType) || isNoValueBranch(block, types))
      ) {
        branches.push({block, container})
      }
    }
  }

  visit(root)
  return branches
}

function normalizeTokens(
  tokens: string | readonly string[] | undefined,
  type: TokenType,
  treatNoValueAsLiteral = false,
) {
  if (tokens === undefined || tokens === '') return {valid: true, tokens: []}

  const values = typeof tokens === 'string' ? [tokens] : tokens
  if (values.some(value => !value || value.trim() !== value)) return {valid: false, tokens: []}

  const normalized = [...new Map(values.map(value => [value.toLowerCase(), value])).values()]
  const noValue = `no:${type}`
  if (!treatNoValueAsLiteral) {
    const noValueIndex = normalized.findIndex(value => value.toLowerCase() === noValue)
    if (noValueIndex !== -1 && normalized.length !== 1) return {valid: false, tokens: []}
    if (noValueIndex !== -1) normalized[noValueIndex] = noValue
  }

  return {valid: true, tokens: normalized}
}

function buildFilterBranch(
  type: TokenType,
  tokens: readonly string[],
  {isNegated = false, treatNoValueAsLiteral = false} = {},
): string {
  if (!treatNoValueAsLiteral && tokens[0] === `no:${type}`) return tokens[0]

  return buildRawBlockString(
    {
      id: 0,
      type: BlockType.Filter,
      key: {value: type},
      provider: createParserProvider(type, {exclusive: true}),
      operator: isNegated ? FilterOperator.IsNot : FilterOperator.Is,
      value: {raw: tokens.join(groupingSupportConfig.valueDelimiter), values: tokens.map(value => ({value}))},
    },
    groupingSupportConfig,
  )
}

function joinQueryParts(...parts: string[]): string {
  return parts.reduce((query, part) => {
    if (!query || !part) return query + part

    const needsBoundary = !/\s$/.test(query) && !/^\s/.test(part) && !query.endsWith('(') && !part.startsWith(')')
    return `${query}${needsBoundary ? ' ' : ''}${part}`
  }, '')
}

function removeBranch(searchQuery: string, branch: BranchOperand): string {
  const {block, container} = branch
  const significant = significantBlocks(container.blocks)
  const operands = significant.filter(candidate => !isBooleanKeyword(candidate))

  if (operands.length === 1 && container.group && container.parent) {
    return removeBranch(searchQuery, {block: container.group, container: container.parent})
  }

  const index = significant.indexOf(block)
  const previous = significant[index - 1]
  const next = significant[index + 1]
  const removesPreviousOperator = isBooleanKeyword(previous)
  const startIndex = removesPreviousOperator ? previous.startIndex : block.startIndex
  const endIndex = removesPreviousOperator ? block.endIndex : isBooleanKeyword(next) ? next.endIndex : block.endIndex
  const exclusiveEndIndex =
    block.type === BlockType.Group && (removesPreviousOperator || !isBooleanKeyword(next)) ? endIndex + 1 : endIndex

  if (container.group) {
    const innerStartIndex = container.group.startIndex + 1
    const innerEndIndex = container.group.endIndex
    const remaining = cleanConsecutiveSpaces(
      joinQueryParts(
        searchQuery.slice(innerStartIndex, startIndex),
        searchQuery.slice(exclusiveEndIndex, innerEndIndex),
      ),
    )
    if (operands.length === 2) {
      return cleanConsecutiveSpaces(
        joinQueryParts(
          searchQuery.slice(0, container.group.startIndex),
          remaining,
          searchQuery.slice(container.group.endIndex + 1),
        ),
      )
    }
    return cleanConsecutiveSpaces(
      joinQueryParts(searchQuery.slice(0, innerStartIndex), remaining, searchQuery.slice(innerEndIndex)),
    )
  }

  return cleanConsecutiveSpaces(joinQueryParts(searchQuery.slice(0, startIndex), searchQuery.slice(exclusiveEndIndex)))
}

function insertBranchBeforeSort(searchQuery: string, blocks: IndexedAnyBlock[], branch: string): string {
  const sortBlock = blocks.find(block => block.type !== BlockType.Group && block.raw.toLowerCase().startsWith('sort:'))
  if (!sortBlock) return cleanConsecutiveSpaces(`${searchQuery} ${branch}`)

  return cleanConsecutiveSpaces(
    `${searchQuery.slice(0, sortBlock.startIndex)}${branch} ${searchQuery.slice(sortBlock.startIndex)}`,
  )
}

function replaceMultipleFlatBranches(searchQuery: string, branches: FilterBranch[], replacement: string): string {
  const queryWithoutBranches = branches
    .toSorted((left, right) => right.block.startIndex - left.block.startIndex)
    .reduce((query, {block}) => query.slice(0, block.startIndex) + query.slice(block.endIndex), searchQuery)
  return cleanConsecutiveSpaces(`${queryWithoutBranches} ${replacement}`)
}

type RenderedOperand = {
  source: string
  changed: boolean
}

type RenderedContainer = {
  source: string
  operandCount: number
  changed: boolean
}

function blockExclusiveEnd(block: IndexedAnyBlock) {
  return block.endIndex + (block.type === BlockType.Group ? 1 : 0)
}

function sourceForBlock(searchQuery: string, block: IndexedAnyBlock) {
  return searchQuery.slice(block.startIndex, blockExclusiveEnd(block))
}

function reconcilePositiveBranchSubset(
  searchQuery: string,
  blocks: IndexedAnyBlock[],
  branches: FilterBranch[],
  selectedTokens: readonly string[],
  type: TokenType,
  treatNoValueAsLiteral: boolean,
): string | undefined {
  const targetBranches = branches.filter(
    ({block}) =>
      !block.key.value.startsWith('-') &&
      (getProviderKey(block.key.value).toLowerCase() === type || isNoValueBranch(block, [type])),
  )
  if (targetBranches.length === 0) return undefined

  const syntheticNoValueIdentity = `no-value:${type}`
  const valueIdentity = (value: string) => `value:${normalizeFilterValue(value).toLowerCase()}`
  const seenValues = new Set<string>()
  const valuesByBlock = new Map<IndexedFilterBlock, Array<{normalized: string; source: string}>>()
  for (const {block} of targetBranches) {
    const values = isNoValueBranch(block, [type])
      ? [{normalized: syntheticNoValueIdentity, source: sourceForBlock(searchQuery, block)}]
      : block.value.values.map(value => {
          const source = searchQuery.slice(value.startIndex, value.endIndex)
          return {normalized: valueIdentity(source), source}
        })
    for (const {normalized} of values) {
      if (!normalizeFilterValue(normalized) || seenValues.has(normalized)) return undefined
      seenValues.add(normalized)
    }
    valuesByBlock.set(block, values)
  }

  const selected = new Set(
    selectedTokens.map(token =>
      !treatNoValueAsLiteral && token.toLowerCase() === `no:${type}` ? syntheticNoValueIdentity : valueIdentity(token),
    ),
  )
  const isStrictSubset = selected.size < seenValues.size && [...selected].every(token => seenValues.has(token))
  const replacesPositiveValuesWithNoValue =
    selected.size === 1 &&
    selected.has(syntheticNoValueIdentity) &&
    !seenValues.has(syntheticNoValueIdentity) &&
    [...seenValues].every(value => value.startsWith('value:'))
  if (!isStrictSubset && !replacesPositiveValuesWithNoValue) return undefined

  const replacements = new Map<IndexedFilterBlock, string | undefined>()
  if (replacesPositiveValuesWithNoValue) {
    const [firstBranch, ...remainingBranches] = targetBranches
    if (!firstBranch) return undefined
    replacements.set(firstBranch.block, buildFilterBranch(type, selectedTokens, {treatNoValueAsLiteral}))
    for (const {block} of remainingBranches) replacements.set(block, undefined)
  }

  for (const {block} of targetBranches) {
    if (replacesPositiveValuesWithNoValue) continue
    const values = valuesByBlock.get(block) ?? []
    const retained = values.filter(value => selected.has(value.normalized))
    if (retained.length === values.length) continue
    if (retained.length === 0) {
      replacements.set(block, undefined)
      continue
    }

    const firstValue = block.value.values[0]
    if (!firstValue) return undefined
    replacements.set(
      block,
      `${searchQuery.slice(block.startIndex, firstValue.startIndex)}${retained.map(value => value.source).join(',')}`,
    )
  }

  const sourceFor = (block: IndexedAnyBlock) => sourceForBlock(searchQuery, block)

  const renderContainer = (containerBlocks: IndexedAnyBlock[], group?: IndexedGroupBlock): RenderedContainer => {
    const significant = significantBlocks(containerBlocks)
    const operands = significant.filter(block => !isBooleanKeyword(block))
    const rendered = new Map<IndexedAnyBlock, RenderedOperand | undefined>()

    for (const block of operands) {
      if (block.type === BlockType.Group) {
        const inner = renderContainer(block.blocks, block)
        rendered.set(
          block,
          !inner.changed
            ? {source: sourceFor(block), changed: false}
            : inner.operandCount === 0
              ? undefined
              : {
                  source: inner.operandCount === 1 ? inner.source : `(${inner.source})`,
                  changed: true,
                },
        )
      } else if (block.type === BlockType.Filter && replacements.has(block)) {
        const replacement = replacements.get(block)
        rendered.set(block, replacement === undefined ? undefined : {source: replacement, changed: true})
      } else {
        rendered.set(block, {source: sourceFor(block), changed: false})
      }
    }

    const changed = operands.some(block => rendered.get(block)?.changed || rendered.get(block) === undefined)
    const containerStart = group ? group.startIndex + 1 : 0
    const containerEnd = group ? group.endIndex : searchQuery.length
    if (!changed) {
      return {
        source: searchQuery.slice(containerStart, containerEnd),
        operandCount: operands.length,
        changed: false,
      }
    }

    if (operands.every(block => rendered.get(block) !== undefined)) {
      const source = operands
        .filter(block => rendered.get(block)?.changed)
        .toSorted((left, right) => right.startIndex - left.startIndex)
        .reduce(
          (current, block) => {
            const replacement = rendered.get(block)?.source ?? ''
            const relativeStart = block.startIndex - containerStart
            const relativeEnd = blockExclusiveEnd(block) - containerStart
            return joinQueryParts(current.slice(0, relativeStart), replacement, current.slice(relativeEnd))
          },
          searchQuery.slice(containerStart, containerEnd),
        )
      return {source, operandCount: operands.length, changed: true}
    }

    const tokens = significant.map(block => ({
      block,
      source: isBooleanKeyword(block) ? sourceFor(block) : rendered.get(block)?.source,
    }))
    for (const operand of operands) {
      if (rendered.get(operand) !== undefined) continue
      const index = tokens.findIndex(token => token.block === operand)
      const previous = tokens[index - 1]?.block
      const next = tokens[index + 1]?.block
      if (isBooleanKeyword(previous)) {
        tokens.splice(index - 1, 2)
      } else if (isBooleanKeyword(next)) {
        tokens.splice(index, 2)
      } else {
        tokens.splice(index, 1)
      }
    }

    return {
      source: tokens
        .map(token => token.source)
        .filter(Boolean)
        .join(' '),
      operandCount: tokens.filter(token => !isBooleanKeyword(token.block)).length,
      changed: true,
    }
  }

  return cleanConsecutiveSpaces(renderContainer(blocks).source)
}

function inspectStructuralQuery(searchQuery: string, matchingTypes: readonly TokenType[]): StructuralQueryInspection {
  const unsafeInspection = {isSafe: false, hasBooleanStructure: true, blocks: []}
  if (searchQuery.length > MAX_QUERY_LENGTH || hasUnsafeQuote(searchQuery)) return unsafeInspection

  const providers = structuralProviderKeys.map(key => createParserProvider(key, {exclusive: true}))
  const parser = new FilterQueryParser(providers, groupingSupportConfig)
  const parserQuery = maskBalancedFilterValueParentheses(searchQuery, parser)
  if (parserQuery === undefined) return unsafeInspection
  const parsed = parser.parse(parserQuery)
  const blocks = parsed.blocks
  if (!blocks.every(isIndexedAnyBlock)) return unsafeInspection

  const hasBooleanStructure = containsBooleanStructure(blocks)
  const isSafe =
    !hasUnsafeStructure(blocks) &&
    !containsEmptyFilter(blocks) &&
    !containsAmbiguousNoValueBranch(blocks, matchingTypes)

  return {isSafe, hasBooleanStructure, blocks}
}

export function inspectFilterBranchesInSearchQuery(
  searchQuery: string,
  matchingTypes: readonly TokenType[],
): FilterBranchQueryInspection {
  const inspection = inspectStructuralQuery(searchQuery, matchingTypes)
  if (!inspection.isSafe) {
    return {
      isSafe: false,
      hasBooleanStructure: inspection.hasBooleanStructure,
      branches: [],
    }
  }

  return {
    isSafe: inspection.isSafe,
    hasBooleanStructure: inspection.hasBooleanStructure,
    branches: findFilterBranches(inspection.blocks, matchingTypes).map(({block}) => ({
      key: getProviderKey(block.key.value).toLowerCase(),
      values: block.value.values.map(value =>
        normalizeFilterValue(searchQuery.slice(value.startIndex, value.endIndex)),
      ),
      isNegated: block.key.value.startsWith('-'),
    })),
  }
}

/**
 * Replaces one unambiguous filter branch without changing its surrounding boolean expression.
 * Callers can opt into consolidating repeated positive branches in structurally flat queries.
 * Multi-select callers can also opt into removing a strict subset of positive values while
 * preserving boolean structure, unrelated operands, and negated branches. Unsafe queries,
 * additions, and ambiguous duplicate values are returned unchanged.
 */
export function replaceFilterBranchInSearchQuery(
  searchQuery: string,
  tokens: string | readonly string[] | undefined,
  type: TokenType,
  options: {
    matchingTypes?: readonly TokenType[]
    replacementType?: TokenType
    isNegated?: boolean
    treatNoValueAsLiteral?: boolean
    allowNegatedMatch?: boolean
    replaceMultipleFlatBranches?: boolean
    reconcilePositiveBranchSubset?: boolean
  } = {},
) {
  const matchingTypes = options.matchingTypes ?? [type]
  const replacementType = options.replacementType ?? type
  const selection = normalizeTokens(tokens, replacementType, options.treatNoValueAsLiteral)
  if (!selection.valid) return searchQuery

  const inspection = inspectStructuralQuery(searchQuery, matchingTypes)
  if (!inspection.isSafe) return searchQuery
  const blocks = inspection.blocks

  const branches = findFilterBranches(blocks, matchingTypes)
  if (
    options.reconcilePositiveBranchSubset &&
    matchingTypes.length === 1 &&
    matchingTypes[0] === type &&
    replacementType === type
  ) {
    const updatedQuery = reconcilePositiveBranchSubset(
      searchQuery,
      blocks,
      branches,
      selection.tokens,
      type,
      options.treatNoValueAsLiteral ?? false,
    )
    if (updatedQuery !== undefined) return updatedQuery.length > MAX_QUERY_LENGTH ? searchQuery : updatedQuery
  }
  if (branches.length > 1) {
    if (
      options.replaceMultipleFlatBranches &&
      !inspection.hasBooleanStructure &&
      matchingTypes.length === 1 &&
      matchingTypes[0] === type &&
      replacementType === type &&
      branches.every(({block}) => !block.key.value.startsWith('-'))
    ) {
      const replacement =
        selection.tokens.length > 0 ? buildFilterBranch(replacementType, selection.tokens, options) : ''
      const updatedQuery = replaceMultipleFlatBranches(searchQuery, branches, replacement)
      return updatedQuery.length > MAX_QUERY_LENGTH ? searchQuery : updatedQuery
    }
    return searchQuery
  }
  if (branches[0]?.block.key.value.startsWith('-') && !options.allowNegatedMatch) {
    return searchQuery
  }

  const branch = branches[0]
  if (!branch) {
    if (
      selection.tokens.length === 0 ||
      blocks.some(block => block.type === BlockType.Keyword && block.raw.toUpperCase() === 'OR')
    ) {
      return searchQuery
    }
    const updatedQuery = insertBranchBeforeSort(
      searchQuery,
      blocks,
      buildFilterBranch(replacementType, selection.tokens, options),
    )
    return updatedQuery.length > MAX_QUERY_LENGTH ? searchQuery : updatedQuery
  }

  if (selection.tokens.length === 0) {
    const updatedQuery = removeBranch(searchQuery, branch)
    return updatedQuery.length > MAX_QUERY_LENGTH ? searchQuery : updatedQuery
  }

  const updatedQuery = cleanConsecutiveSpaces(
    joinQueryParts(
      searchQuery.slice(0, branch.block.startIndex),
      buildFilterBranch(replacementType, selection.tokens, options),
      searchQuery.slice(branch.block.endIndex),
    ),
  )
  return updatedQuery.length > MAX_QUERY_LENGTH ? searchQuery : updatedQuery
}
